type Env = 'development' | 'staging' | 'demo' | 'production';

type IGetEnvFeatureFlag = () => {
  env: Env;
  enableDevelopment: boolean;
  enableStaging: boolean;
};

const STAGING_PATTERN = /^(?:stg|qa-\d+)\.fukuri-client-admin-dev\.jp$/;
const DEMO_HOSTNAME = 'demo.fukuri-client-admin-dev.jp';
const PRODUCTION_HOSTNAMES = ['cl.fukuri-app.jp'];

const getEnv = (hostname: string): Env => {
  if (STAGING_PATTERN.test(hostname)) return 'staging';
  if (hostname === DEMO_HOSTNAME) return 'demo';
  if (PRODUCTION_HOSTNAMES.includes(hostname)) return 'production';
  return 'development';
};

/**
 * 実行中のホスト名から環境を判定し、環境別の機能フラグを返します。
 *
 * | 環境 | enableDevelopment | enableStaging |
 * |------|:-:|:-:|
 * | development | ✅ | ✅ |
 * | staging (stg / qa-N) | ❌ | ✅ |
 * | demo | ❌ | ❌ |
 * | production（一般） | ❌ | ❌ |
 *
 * - `env`: 現在の環境（`development` | `staging` | `demo` | `production`）
 * - `enableDevelopment`: development 環境で `true`
 * - `enableStaging`: development / staging で `true`（demo / production では `false`）
 *
 * 本フラグは「開発中の機能を main にマージし、環境ごとに段階的に有効化する」用途を想定しています。
 * テナント単位での機能出し分け（例: アクティベーションコード）は、ハードコードではなく
 * PartnerFeatureFlag で制御します（MMT-156 で `enableInternal` からの移行を実施）。
 *
 * @remarks
 * - SSR/Node 環境では `window` が未定義のため、クライアント実行前提で呼び出してください。
 * - ドメイン構成が変わった場合は `getEnv` のマッピングを更新してください。
 *
 * @returns 環境名と、それに応じたブールフラグのオブジェクト
 */
export const getEnvFeatureFlag: IGetEnvFeatureFlag = () => {
  const hostname = window.location.hostname;

  const env = getEnv(hostname);

  const enableDevelopment = env === 'development';
  const enableStaging = env === 'development' || env === 'staging';

  return {
    env,
    enableDevelopment,
    enableStaging,
  };
};
